import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { PackageOpen, ArrowUpRight, ChevronRight, Loader2, BookOpen } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (!u ? "" : u.startsWith("/api") ? `${BACKEND}${u}` : u);

function embedUrl(u) {
  if (!u) return "";
  try {
    if (u.includes("youtube.com/watch")) return `https://www.youtube.com/embed/${new URL(u).searchParams.get("v")}`;
    if (u.includes("youtu.be/")) return `https://www.youtube.com/embed/${u.split("youtu.be/")[1].split(/[?&]/)[0]}`;
    if (u.includes("youtube.com/embed")) return u;
    if (u.includes("vimeo.com/")) { const id = u.split("vimeo.com/")[1].split(/[?&/]/)[0]; return `https://player.vimeo.com/video/${id}`; }
    if (u.includes("loom.com/share/")) return u.replace("loom.com/share/", "loom.com/embed/");
    if (u.includes("loom.com/embed")) return u;
  } catch { /* noop */ }
  return u;
}

function setMeta(title, desc) {
  if (title) document.title = title;
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) { tag = document.createElement("meta"); tag.name = "description"; document.head.appendChild(tag); }
  tag.content = desc || "";
}

function Block({ b }) {
  if (b.type === "heading") return <h2 className="font-head text-2xl md:text-3xl font-bold text-white mt-8 mb-3" data-testid="learn-block-heading">{b.content}</h2>;
  if (b.type === "text") return <p className="text-[#C4C4C4] leading-relaxed whitespace-pre-wrap mb-4 text-[15px]" data-testid="learn-block-text">{b.content}</p>;
  if (b.type === "callout") return (
    <div className="my-5 rounded-xl border border-[#007AFF]/30 bg-[#007AFF]/10 px-4 py-3 text-[#cfe4ff] text-sm" data-testid="learn-block-callout">{b.content}</div>
  );
  if (b.type === "image") return (
    <figure className="my-6" data-testid="learn-block-image">
      <img src={mediaUrl(b.url)} alt={b.caption || ""} className="w-full rounded-xl border border-[#1e1e1e]" loading="lazy" />
      {b.caption && <figcaption className="text-xs text-[#666] mt-2 text-center">{b.caption}</figcaption>}
    </figure>
  );
  if (b.type === "video") return (
    <figure className="my-6" data-testid="learn-block-video">
      <div className="relative w-full rounded-xl overflow-hidden border border-[#1e1e1e]" style={{ paddingTop: "56.25%" }}>
        <iframe src={embedUrl(b.url)} title={b.caption || "video"} className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
      {b.caption && <figcaption className="text-xs text-[#666] mt-2 text-center">{b.caption}</figcaption>}
    </figure>
  );
  return null;
}

export default function Learn() {
  const { sectionSlug, pageSlug } = useParams();
  const navigate = useNavigate();
  const [nav, setNav] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cms/nav").then((r) => setNav(r.data.sections || [])).catch(() => setNav([]));
  }, []);

  useEffect(() => {
    if (sectionSlug && pageSlug) return; // page effect handles it
    if (nav.length && nav[0].pages.length) {
      navigate(`/learn/${nav[0].slug}/${nav[0].pages[0].slug}`, { replace: true });
    } else if (nav.length === 0) {
      setLoading(false);
    }
  }, [nav, sectionSlug, pageSlug, navigate]);

  useEffect(() => {
    if (!sectionSlug || !pageSlug) return;
    setLoading(true);
    api.get(`/cms/page/${sectionSlug}/${pageSlug}`)
      .then((r) => { setPage(r.data); setMeta(r.data.seo_title || `${r.data.title} · FBAStar`, r.data.seo_description || r.data.excerpt); setLoading(false); })
      .catch(() => { setPage(null); setLoading(false); });
  }, [sectionSlug, pageSlug]);

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-4 md:px-8 border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <Link to="/learn" className="flex items-center gap-2" data-testid="learn-logo">
          <PackageOpen className="h-6 w-6 text-[#007AFF]" />
          <span className="font-head font-extrabold text-lg tracking-tight">FBAStar</span>
          <span className="text-[#666] text-sm font-medium hidden sm:inline">· Help Center</span>
        </Link>
        <a href="/" data-testid="learn-open-app" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#007AFF] hover:bg-[#005BB5] text-white text-sm font-semibold transition-colors">
          Open App <ArrowUpRight className="h-4 w-4" />
        </a>
      </header>

      <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-8 px-4 md:px-8 py-8">
        <aside className="md:w-64 shrink-0" data-testid="learn-nav">
          <nav className="space-y-5 md:sticky md:top-24">
            {nav.map((sec) => (
              <div key={sec.slug}>
                <p className="px-1 mb-2 text-[10px] font-bold uppercase tracking-widest text-[#555]">{sec.title}</p>
                <div className="space-y-0.5">
                  {sec.pages.map((p) => {
                    const active = sec.slug === sectionSlug && p.slug === pageSlug;
                    return (
                      <Link
                        key={p.slug}
                        to={`/learn/${sec.slug}/${p.slug}`}
                        data-testid={`learn-nav-${sec.slug}-${p.slug}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-[#007AFF]/15 text-white font-semibold" : "text-[#A3A3A3] hover:text-white hover:bg-white/5"}`}
                      >
                        <ChevronRight className={`h-3.5 w-3.5 ${active ? "text-[#007AFF]" : "opacity-40"}`} />
                        {p.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
            {!nav.length && <p className="text-sm text-[#666] px-1">No guides published yet.</p>}
          </nav>
        </aside>

        <main className="flex-1 min-w-0" data-testid="learn-content">
          {loading ? (
            <div className="flex items-center justify-center h-64 text-[#666]"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : !page ? (
            <div className="rounded-xl border border-[#1e1e1e] bg-[#0f0f0f] p-10 text-center">
              <BookOpen className="h-10 w-10 text-[#333] mx-auto mb-3" />
              <p className="text-[#A3A3A3]">No guide selected. Pick a topic from the left.</p>
            </div>
          ) : (
            <article>
              <p className="text-xs uppercase tracking-widest text-[#007AFF] font-semibold mb-2">{page.section_title}</p>
              <h1 className="font-head text-3xl md:text-4xl font-black tracking-tight mb-4">{page.title}</h1>
              {page.excerpt && <p className="text-[#A3A3A3] text-lg mb-6">{page.excerpt}</p>}
              {page.hero_image && <img src={mediaUrl(page.hero_image)} alt={page.title} className="w-full rounded-2xl border border-[#1e1e1e] mb-6" loading="lazy" />}
              <div>{(page.blocks || []).map((b, i) => <Block key={i} b={b} />)}</div>
            </article>
          )}
        </main>
      </div>
    </div>
  );
}
